import React from "react";
import { useTranslation } from "../../hooks/useTranslation";

/**
 * Dinezi Privacy Policy React component
 * - Single-file component using Tailwind CSS for styling
 * - Default export
 * - Dynamic current year
 * - Collapsible sections with anchors for quick navigation
 * - Lightweight and accessible (semantic HTML)
 *
 * Usage:
 * <DineziPrivacyPolicy />
 */

export default function DineziPrivacyPolicy() {
    const { t } = useTranslation();
    const year = new Date().getFullYear();
    const privacy = t("privacyPolicy");

    const sections = [
        { id: "overview", title: privacy.sections.overview.title },
        { id: "data-collection", title: privacy.sections.dataCollection.title },
        { id: "data-usage", title: privacy.sections.dataUsage.title },
        { id: "data-sharing", title: privacy.sections.dataSharing.title },
        { id: "data-security", title: privacy.sections.dataSecurity.title },
        { id: "location-data", title: privacy.sections.locationData.title },
        { id: "cookies", title: privacy.sections.cookies.title },
        { id: "user-rights", title: privacy.sections.userRights.title },
        { id: "children", title: privacy.sections.children.title },
        { id: "international", title: privacy.sections.international.title },
        { id: "changes", title: privacy.sections.changes.title },
        { id: "contact", title: privacy.sections.contact.title },
    ];

    return (
        <div className="min-h-screen bg-[#FAFAFA]">
            <div className="container max-w-6xl mx-auto px-4 py-6 sm:px-6 sm:py-10">
                <header className="text-center mb-6 sm:mb-12">
                    <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-4">{privacy.title}</h1>
                    {/* <div className="flex items-center justify-center space-x-2 text-sm text-gray-600">
                        <span className="px-3 py-1 bg-gray-100 rounded-full">Last updated: {lastUpdated}</span>
                        <span className="w-1.5 h-1.5 bg-gray-300 rounded-full"></span>
                        <span className="px-3 py-1 bg-gray-100 rounded-full">Version 1.0</span>
                    </div> */}
                </header>

                <div className="flex flex-col lg:flex-row gap-4 sm:gap-8">
                    <nav className="w-full lg:w-80 lg:sticky lg:top-6 self-start order-2 lg:order-1">
                        <div className="bg-white p-4 sm:p-6 rounded-xl sm:rounded-2xl shadow-lg border border-gray-100">
                            <h2 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4 text-gray-900">{privacy.quickNavigation}</h2>
                            <ul className="space-y-2">
                                {sections.map((s) => (
                                    <li key={s.id}>
                                        <a
                                            className="flex items-center px-4 py-2 text-gray-700 hover:bg-blue-50 hover:text-blue-600 rounded-lg transition-colors duration-150"
                                            href={`#${s.id}`}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                const el = document.getElementById(s.id);
                                                if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                                            }}
                                        >
                                            <span className="text-sm">{s.title}</span>
                                        </a>
                                    </li>
                                ))}
                            </ul>

                            <div className="mt-6 pt-6 border-t border-gray-100">
                                <p className="text-xs text-gray-500 text-center" dangerouslySetInnerHTML={{ __html: privacy.copyright.replace("{{year}}", year) }} />
                            </div>
                        </div>
                    </nav>

                    {/* Main content */}
                    <main className="flex-1 bg-white rounded-xl sm:rounded-2xl shadow-lg border border-gray-100 p-4 sm:p-6 md:p-8 order-1 lg:order-2">
                        <div className="prose prose-sm sm:prose-base md:prose-lg max-w-none">
                            <div className="bg-blue-50 border-l-4 border-blue-500 p-4 sm:p-6 mb-6 sm:mb-8 rounded-r-lg">
                                <p className="text-sm sm:text-base text-blue-900 leading-relaxed" dangerouslySetInnerHTML={{ __html: privacy.intro }} />
                            </div>
                        </div>

                        <section id="overview" className="mb-8 sm:mb-12">
                            <div className="flex items-center space-x-2 sm:space-x-3 mb-4 sm:mb-6">
                                <div className="flex-shrink-0 w-6 h-6 sm:w-8 sm:h-8 bg-blue-100 rounded-full flex items-center justify-center">
                                    <span className="text-xs sm:text-sm text-blue-600 font-semibold">1</span>
                                </div>
                                <h3 className="text-xl sm:text-2xl font-bold text-gray-900">{privacy.sections.overview.title}</h3>
                            </div>
                            <p className="text-sm sm:text-base">
                                {privacy.sections.overview.content}
                            </p>
                        </section>

                        <section id="data-collection" className="mb-6 sm:mb-8">
                            <h3 className="text-lg sm:text-xl font-semibold mb-2 sm:mb-3">{privacy.sections.dataCollection.title}</h3>
                            <p className="text-sm sm:text-base mb-2 sm:mb-3">{privacy.sections.dataCollection.intro}</p>
                            <ul>
                                <li><strong>{privacy.sections.dataCollection.personalInfo}</strong> {privacy.sections.dataCollection.personalInfoDesc}</li>
                                <li><strong>{privacy.sections.dataCollection.accountInfo}</strong> {privacy.sections.dataCollection.accountInfoDesc}</li>
                                <li><strong>{privacy.sections.dataCollection.locationData}</strong> {privacy.sections.dataCollection.locationDataDesc}</li>
                                <li><strong>{privacy.sections.dataCollection.usageData}</strong> {privacy.sections.dataCollection.usageDataDesc}</li>
                                <li><strong>{privacy.sections.dataCollection.transactionData}</strong> {privacy.sections.dataCollection.transactionDataDesc}</li>
                                <li><strong>{privacy.sections.dataCollection.deviceInfo}</strong> {privacy.sections.dataCollection.deviceInfoDesc}</li>
                            </ul>
                        </section>

                        <section id="data-usage" className="mb-6 sm:mb-8">
                            <h3 className="text-lg sm:text-xl font-semibold mb-2 sm:mb-3">{privacy.sections.dataUsage.title}</h3>
                            <p className="text-sm sm:text-base mb-2 sm:mb-3">{privacy.sections.dataUsage.intro}</p>
                            <ul>
                                <li>{privacy.sections.dataUsage.items.bookings}</li>
                                <li>{privacy.sections.dataUsage.items.support}</li>
                                <li>{privacy.sections.dataUsage.items.confirmations}</li>
                                <li>{privacy.sections.dataUsage.items.improve}</li>
                                <li>{privacy.sections.dataUsage.items.fraud}</li>
                                <li>{privacy.sections.dataUsage.items.promotions}</li>
                            </ul>
                        </section>

                        <section id="data-sharing" className="mb-6 sm:mb-8">
                            <h3 className="text-lg sm:text-xl font-semibold mb-2 sm:mb-3">{privacy.sections.dataSharing.title}</h3>
                            <p className="text-sm sm:text-base mb-2 sm:mb-3">
                                {privacy.sections.dataSharing.intro}
                            </p>
                            <ul>
                                <li><strong>{privacy.sections.dataSharing.restaurants}</strong> {privacy.sections.dataSharing.restaurantsDesc}</li>
                                <li><strong>{privacy.sections.dataSharing.paymentProcessors}</strong> {privacy.sections.dataSharing.paymentProcessorsDesc}</li>
                                <li><strong>{privacy.sections.dataSharing.serviceProviders}</strong> {privacy.sections.dataSharing.serviceProvidersDesc}</li>
                            </ul>
                            <p className="text-sm sm:text-base">
                                {privacy.sections.dataSharing.noSale}
                            </p>
                        </section>

                        <section id="data-security" className="mb-6 sm:mb-8">
                            <h3 className="text-lg sm:text-xl font-semibold mb-2 sm:mb-3">{privacy.sections.dataSecurity.title}</h3>
                            <p className="text-sm sm:text-base mb-2 sm:mb-3">
                                {privacy.sections.dataSecurity.intro}
                            </p>
                            <ul>
                                <li>{privacy.sections.dataSecurity.encryption}</li>
                                <li>{privacy.sections.dataSecurity.assessments}</li>
                                <li>{privacy.sections.dataSecurity.accessControls}</li>
                                <li>{privacy.sections.dataSecurity.storage}</li>
                            </ul>
                        </section>

                        <section id="location-data" className="mb-6 sm:mb-8">
                            <h3 className="text-lg sm:text-xl font-semibold mb-2 sm:mb-3">{privacy.sections.locationData.title}</h3>
                            <p className="text-sm sm:text-base">
                                {privacy.sections.locationData.content}
                            </p>
                        </section>

                        <section id="cookies" className="mb-6 sm:mb-8">
                            <h3 className="text-lg sm:text-xl font-semibold mb-2 sm:mb-3">{privacy.sections.cookies.title}</h3>
                            <p className="text-sm sm:text-base mb-2 sm:mb-3">
                                {privacy.sections.cookies.intro}
                            </p>
                            <ul>
                                <li>{privacy.sections.cookies.preferences}</li>
                                <li>{privacy.sections.cookies.understandUsage}</li>
                                <li>{privacy.sections.cookies.improveUX}</li>
                                <li>{privacy.sections.cookies.personalized}</li>
                            </ul>
                        </section>

                        <section id="user-rights" className="mb-6 sm:mb-8">
                            <h3 className="text-lg sm:text-xl font-semibold mb-2 sm:mb-3">{privacy.sections.userRights.title}</h3>
                            <p className="text-sm sm:text-base mb-2 sm:mb-3">
                                {privacy.sections.userRights.intro}
                            </p>
                            <ul>
                                <li>{privacy.sections.userRights.access}</li>
                                <li>{privacy.sections.userRights.correct}</li>
                                <li>{privacy.sections.userRights.delete}</li>
                                <li>{privacy.sections.userRights.object}</li>
                                <li>{privacy.sections.userRights.withdraw}</li>
                                <li>{privacy.sections.userRights.portability}</li>
                            </ul>
                        </section>

                        <section id="children" className="mb-6 sm:mb-8">
                            <h3 className="text-lg sm:text-xl font-semibold mb-2 sm:mb-3">{privacy.sections.children.title}</h3>
                            <p className="text-sm sm:text-base">
                                {privacy.sections.children.content}
                            </p>
                        </section>

                        <section id="international" className="mb-6 sm:mb-8">
                            <h3 className="text-lg sm:text-xl font-semibold mb-2 sm:mb-3">{privacy.sections.international.title}</h3>
                            <p className="text-sm sm:text-base">
                                {privacy.sections.international.content}
                            </p>
                        </section>

                        <section id="changes" className="mb-6 sm:mb-8">
                            <h3 className="text-lg sm:text-xl font-semibold mb-2 sm:mb-3">{privacy.sections.changes.title}</h3>
                            <p className="text-sm sm:text-base">
                                {privacy.sections.changes.content}
                            </p>
                        </section>

                        {/* <section id="contact">
                            <h3>12. Contact Us</h3>
                            <p>
                                If you have questions about this Privacy Policy, contact us at:
                                <br />
                                <a className="text-blue-600 hover:underline" href="mailto:privacy@dinezi.com">
                                    privacy@dinezi.com
                                </a>
                            </p>
                        </section> */}

                        <div className="mt-8 sm:mt-12 border-t border-gray-100 pt-6 sm:pt-8">
                            <div className="bg-gray-50 rounded-xl p-4 sm:p-6">
                                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4">
                                    <label className="flex items-start sm:items-center gap-2 sm:gap-3">
                                        <input
                                            type="checkbox"
                                            aria-label="accept-privacy"
                                            className="w-4 h-4 sm:w-5 sm:h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 mt-0.5 sm:mt-0"
                                        />
                                        <span className="text-xs sm:text-sm text-gray-700">{privacy.agreeCheckbox}</span>
                                    </label>

                                    {/* <div className="flex items-center gap-4">
                                        <button
                                            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white rounded-lg border border-gray-200 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                            onClick={() => window.print()}
                                        >
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                                            </svg>
                                            Print Policy
                                        </button>
                                        <a
                                            href="mailto:privacy@dinezi.com"
                                            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                        >
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                            </svg>
                                            Contact Privacy Team
                                        </a>
                                    </div> */}
                                </div>
                            </div>
                        </div>
                    </main>
                </div>
            </div>
        </div>
    );
}
